/**
 * ADP Submission Preview — standalone report showing what would be sent to ADP.
 *
 * Consumes the existing payroll-preview endpoint (no separate backend).
 * Presents the data in ADP's format: grouped by worker with ADP identifiers,
 * earning lines, and totals. Exportable to CSV for manual ADP comparison.
 */

import { api, type PayrollPreview, type PayrollPreviewLine } from '../api';
import { UnifiedTimeFilter } from '../components/UnifiedTimeFilter';
import { useTimeFilter } from '../context/TimeFilterContext';
import { Card, CardHeader, Cell, ExportButton, Row, Spinner, Table } from '../components/Ui';
import { hours, money, moneyCompact, shortDate } from '../format';
import { useAsyncData } from '../hooks/useAsyncData';
import { Shell, type ShellProps } from '../Shell';

export function AdpPreview(props: ShellProps) {
  const tf = useTimeFilter();

  const { data, error, loading } = useAsyncData<PayrollPreview>(
    () => api.payrollPreview(tf.toPeriodId(), { scope: 'all' }),
    [tf.selection],
    'Could not load payroll preview.',
  );

  const missingAdp = data?.lines.filter((l) => !l.adpCompanyCode || !l.adpFileNumber) ?? [];
  const adpLines = data?.lines ?? [];

  const csvRows = adpLines.flatMap((w) =>
    w.earningBreakdown.map((e) => ({
      adpCompanyCode: w.adpCompanyCode ?? '',
      adpFileNumber: w.adpFileNumber ?? '',
      workerName: w.workerName,
      earningType: e.label,
      hours: e.hours,
      payRate: w.payRate,
      payAmount: e.pay,
    })),
  );

  return (
    <Shell {...props} title="ADP Preview">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-ink">ADP Submission Preview</h1>
          <p className="mt-1 text-sm text-ink-faint">
            What would be submitted to ADP for the selected period
          </p>
        </div>
        <UnifiedTimeFilter />
      </div>

      <Card className="mb-4 border-l-4 border-l-caution">
        <div className="px-4 py-3">
          <p className="text-sm font-semibold text-ink">Preview Only</p>
          <p className="mt-1 text-sm text-ink-muted">
            This report shows what would be submitted to ADP. No data has been sent. Use this to
            verify payroll data before manual ADP entry or before enabling automated submission.
          </p>
        </div>
      </Card>

      {error && (
        <Card className="mb-4">
          <div className="px-4 py-8 text-center text-sm text-caution">{error}</div>
        </Card>
      )}

      {loading && (
        <Card>
          <Spinner label="Loading payroll preview…" />
        </Card>
      )}

      {data && !loading && (
        <>
          <div className="mb-4 grid grid-cols-2 gap-4 md:grid-cols-4">
            <SummaryKpi label="Workers" value={String(adpLines.length)} />
            <SummaryKpi label="Total Hours" value={hours(data.totals.totalHours)} />
            <SummaryKpi
              label="Total Pay"
              value={moneyCompact(data.totals.totalPay)}
              exact={money(data.totals.totalPay)}
              tone="profit"
            />
            {data.summary.deltaPct !== null && (
              <SummaryKpi
                label="vs Prior Period"
                value={`${Number(data.summary.deltaPct) >= 0 ? '+' : ''}${data.summary.deltaPct}%`}
                {...(Math.abs(Number(data.summary.deltaPct)) > 15
                  ? { tone: 'caution' as const }
                  : {})}
              />
            )}
          </div>

          {missingAdp.length > 0 && (
            <Card className="mb-4 border-l-4 border-l-red-500">
              <div className="px-4 py-3">
                <p className="text-sm font-semibold text-red-700">
                  {missingAdp.length} worker{missingAdp.length === 1 ? '' : 's'} missing ADP
                  identifiers
                </p>
                <ul className="mt-1 list-disc pl-5 text-sm text-ink-muted">
                  {missingAdp.map((w) => (
                    <li key={w.workerId}>
                      {w.workerName}
                      {!w.adpCompanyCode && ' — missing Company Code'}
                      {!w.adpFileNumber && ' — missing File Number'}
                    </li>
                  ))}
                </ul>
              </div>
            </Card>
          )}

          <Card>
            <CardHeader
              title="Payroll Lines"
              subtitle={`${data.period.periodStart ? `${shortDate(data.period.periodStart)} – ${shortDate(data.period.periodEnd)}` : ''}`}
              aside={
                <ExportButton
                  data={csvRows}
                  filename={`adp-preview-${data.period.periodStart ?? 'current'}`}
                  headers={[
                    { key: 'adpCompanyCode', label: 'ADP Co Code' },
                    { key: 'adpFileNumber', label: 'ADP File #' },
                    { key: 'workerName', label: 'Worker Name' },
                    { key: 'earningType', label: 'Earning Type' },
                    { key: 'hours', label: 'Hours' },
                    { key: 'payRate', label: 'Pay Rate' },
                    { key: 'payAmount', label: 'Pay Amount' },
                  ]}
                />
              }
            />
            <Table
              head={[
                { label: 'ADP Co Code' },
                { label: 'File #' },
                { label: 'Worker Name' },
                { label: 'Earning Type' },
                { label: 'Hours', align: 'right' },
                { label: 'Pay Rate', align: 'right' },
                { label: 'Pay Amount', align: 'right' },
              ]}
            >
              {adpLines.map((w) => (
                <WorkerGroup key={w.workerId} worker={w} />
              ))}
              <tr className="border-t-2 border-edge bg-surface-overlay font-semibold">
                <td className="px-3 py-2" colSpan={4}>
                  Grand Total
                </td>
                <td className="px-3 py-2 text-right font-mono">
                  {hours(data.totals.payableHours)}
                </td>
                <td />
                <td className="px-3 py-2 text-right font-mono text-profit">
                  {money(data.totals.totalPay)}
                </td>
              </tr>
            </Table>
          </Card>
        </>
      )}
    </Shell>
  );
}

function WorkerGroup({ worker: w }: { worker: PayrollPreviewLine }) {
  const missingIds = !w.adpCompanyCode || !w.adpFileNumber;
  return (
    <>
      {w.earningBreakdown.map((e, i) => (
        <Row key={`${w.workerId}-${e.category}`}>
          {i === 0 ? (
            <>
              <Cell muted>{w.adpCompanyCode ?? '—'}</Cell>
              <Cell muted>{w.adpFileNumber ?? '—'}</Cell>
              <Cell>
                <span className={missingIds ? 'text-red-600' : ''}>
                  {w.workerName}
                  {w.warnings.length > 0 && (
                    <span className="ml-2 text-[10px] text-caution">{w.warnings.join(', ')}</span>
                  )}
                </span>
              </Cell>
            </>
          ) : (
            <>
              <Cell>{''}</Cell>
              <Cell>{''}</Cell>
              <Cell>{''}</Cell>
            </>
          )}
          <Cell muted>{e.label}</Cell>
          <Cell align="right" mono>
            {hours(e.hours)}
          </Cell>
          <Cell align="right" mono muted>
            {money(w.payRate)}
          </Cell>
          <Cell align="right" mono>
            {money(e.pay)}
          </Cell>
        </Row>
      ))}
      {w.earningBreakdown.length > 1 && (
        <tr className="border-b border-edge/50 bg-surface-overlay/30">
          <td className="px-3 py-1" colSpan={4}>
            <span className="text-xs text-ink-faint italic">Subtotal — {w.workerName}</span>
          </td>
          <td className="px-3 py-1 text-right font-mono text-xs text-ink-muted">
            {hours(w.totalHours)}
          </td>
          <td />
          <td className="px-3 py-1 text-right font-mono text-xs font-semibold text-ink-muted">
            {money(w.totalPay)}
          </td>
        </tr>
      )}
    </>
  );
}

function SummaryKpi({
  label,
  value,
  exact,
  tone,
}: {
  label: string;
  value: string;
  exact?: string;
  tone?: 'profit' | 'caution';
}) {
  return (
    <div className="rounded-lg bg-surface-raised px-4 py-3">
      <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-ink-faint">{label}</p>
      <p
        className={`mt-1 text-lg font-semibold ${tone === 'profit' ? 'text-profit' : tone === 'caution' ? 'text-caution' : 'text-ink'}`}
        title={exact}
      >
        {value}
      </p>
    </div>
  );
}
